import { useTranslation } from "@pancakeswap/localization"
import { AddIcon, Balance, Box, Button, ButtonMenu, ButtonMenuItem, Card, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, CopyButton, CopyIcon, Flex, FlexGap, IconButton, InfoIcon, MinusIcon, MoreIcon, OpenNewIcon, Progress, ProgressBar, QuestionHelper, Svg, SvgProps, SyncAltIcon, Text, useModal } from "@pancakeswap/uikit"
import { formatDecimals } from "@pancakeswap/utils/formatBalance"
import { CurrencyLogo, DoubleCurrencyLogo, TripleCurrencyLogo } from "components/Logo"
import { usePoolTokenPercentage, useToken0Token1USDValue, useTokensDeposited, useTotalUSDValue } from "components/PositionCard"
import { useCurrency } from "hooks/Tokens"
import Link from "next/link"
import { useMemo, useState, useCallback } from "react"
import styled from "styled-components"
import BigNumber from "bignumber.js"
import { ChainId, CurrencyAmount, Pair, Token } from "@pancakeswap/sdk"
import { useAccount } from "wagmi"
import { TOTAL_FEE } from "config/constants/info"
import useTotalSupply from "hooks/useTotalSupply"
import { useLPApr } from "state/swap/useLPApr"
import useTokenBalance from "hooks/useTokenBalance"
import AddToWalletButton from "components/AddToWallet/AddToWalletButton"
import { useTokens } from "views/Swap/hooks/useTokens"
import { safeGetAddress } from "utils"
import DecreaseModalStable from './DecreaseModalStable'
import AddModalStable from './AddModalStable'
import { PoolData } from "../hooks/usePools"

const Panel = styled.div`
    // border: 1px solid #403d3f;
    background: #FFFFFF0D;
    // box-shadow: #fff 0px 2px 20px;
    border-radius: 16px;
    margin-top: 16px;
    padding: 16px 20px;   
`

const Hr = styled.hr`
    margin-top: 16px;
    border-color: #706587;
`

const FeeTier = styled(Text)`
    border: 1px solid ${({ theme }) => theme.colors.textSubtle};
    border-radius: 12px;
    padding: 0 6px;
    font-size: 12px;
    line-height: 12px;
    opacity: 0.5;
`

const Version = styled(Text)`
    border-radius: 12px;
    padding: 0 6px;
    font-size: 12px;
    line-height: 12px;
    background: ${({ theme }) => theme.colors.text99};
    color: ${({ theme }) => theme.colors.backgroundAlt};
`

const OutOfRange = styled(Text)`
    border-radius: 12px;
    padding: 2px 6px;
    font-size: 12px;
    line-height: 12px;
    background: ${({ theme }) => theme.colors.textSubtle};
    color: ${({ theme }) => theme.colors.backgroundAlt};
`

const InRange = styled(Text)`
    border-radius: 12px;
    padding: 2px 6px;
    font-size: 12px;
    line-height: 12px;
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.backgroundAlt};
`

const StyledIconButton = styled(IconButton)`
    background: #B7ADD11A;
    padding: 8px;
    height: 40px;
    width: 40px;
    border-radius: 20px;
`

const CreateButton = styled(Button)`
    padding: 12px 20px;
    font-family: 'IBM Plex Sans';
    font-family: 'IBM Plex Sans';
    font-size: 14px;
    height: auto;
    border-radius: 20px;
`

const ExpandButton = styled(Button)`
    padding: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.textSubtle};
    stroke: ${({ theme }) => theme.colors.textSubtle};
`

const OutlinedButton = styled(Button)`
    font-size: 14px;
    border-radius: 20px;
    width: 100%;
    padding: 10px;
    height: auto;
    margin-top: 1em;
`

const ExpandIcon: React.FC<React.PropsWithChildren<SvgProps>> = (props) => {
    return (
        <Svg viewBox="0 0 25 24" {...props}>
            <path d="M9 9L4 4M4 4V8M4 4H8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15 9L20 4M20 4V8M20 4H16" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 15L4 20M4 20V16M4 20H8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15 15L20 20M20 20V16M20 20H16" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </Svg>
    );
};

const CollapseIcon: React.FC<React.PropsWithChildren<SvgProps>> = (props) => {
    return (
        <Svg viewBox="0 0 25 24" {...props}>
            <path d="M20 20L15 15M15 15V19M15 15H19" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 20L9 15M9 15V19M9 15H5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20 4L15 9M15 9V5M15 9H19" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 4L9 9M9 9V5M9 9H5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </Svg>
    );
};

const formatNumber = (value, precision = 6) => {
    if (!value) return '0'

    const k = 1000
    const sizes = ['', 'K', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y']

    const i = value < 1 ? 0 : Math.floor(Math.log(value) / Math.log(k))

    return `${parseFloat((value / k ** i).toFixed(i ? 2 : precision))}${sizes[i]}`
}

const RightBox = styled(Box)`
    border-left: 1px solid #B4899E3D;
    height: 100%;
    display: flex;
    align-items: center;
`

const StablePosition = ({ pool, expanded, setExpanded }: { pool: PoolData, expanded: boolean, setExpanded: (expand: boolean) => void }) => {
    const { t } = useTranslation()

    const currency0 = useCurrency(pool.token0.address)
    const currency1 = useCurrency(pool.token1.address)
    const currency2 = useCurrency(pool.token2?.address)

    // console.log("pool.token0.address", pool.token0.address)
    // console.log("pool.token1.address", pool.token1.address)
    // console.log("pool.token2?.address", pool.token2?.address)

    const lpToken = new Token(ChainId.PULSE, pool.address, 18, 'pStable LP')

    const { balance } = useTokenBalance(pool.address)
    const userPoolBalance = CurrencyAmount.fromRawAmount(lpToken, balance?.toString() ?? 0n)
    const totalPoolTokens = useTotalSupply(lpToken)

    const share = userPoolBalance && totalPoolTokens ? Number(userPoolBalance.numerator) / Number(totalPoolTokens.numerator) : 0

    const { data: tokenDatas } = useTokens([
        pool.token0.address,
        pool.token1.address,
        pool.token2?.address
    ])

    const token0Deposited = Number(pool.reserve0) * share
    const token1Deposited = Number(pool.reserve1) * share
    const token2Deposited = Number(pool.reserve2) * share

    const userBalance = Number(userPoolBalance?.toExact() ?? 0)

    const [totalUSDValue] = useMemo(() => {
        if (!tokenDatas)
            return [0]
        const token0USD = Number(pool.reserve0) * share * (tokenDatas.find(_t => safeGetAddress(_t.address) === pool.token0.address)?.price ?? 0)
        const token1USD = Number(pool.reserve1) * share * (tokenDatas.find(_t => safeGetAddress(_t.address) === pool.token1.address)?.price ?? 0)
        const token2USD = Number(pool.reserve2) * share * (tokenDatas.find(_t => safeGetAddress(_t.address) === pool.token2.address)?.price ?? 0)
        return [token0USD + token1USD + token2USD, token0USD, token1USD, token2USD]
    }, [tokenDatas, pool, share])

    const [onPresentAddModal] = useModal(
        <AddModalStable />,
        true,
        true,
        `addModalStable-${pool.address}`,
    )

    const handleIncreaseModal = useCallback((e: any) => {
        e?.stopPropagation?.()
        onPresentAddModal()
    }, [onPresentAddModal])

    const [onPresentDecreaseModal] = useModal(
        <DecreaseModalStable pool={pool} />,
        true,
        true,
        `decreaseModalStable-${pool.address}`,
    )

    const handleOpenDecreaseModal = useCallback((e: any) => {
        e?.stopPropagation?.()
        onPresentDecreaseModal()
    }, [onPresentDecreaseModal])

    if (!userPoolBalance || userPoolBalance.equalTo(0n))
        return null

    return (
        <Panel>
            <FlexGap onClick={() => setExpanded(true)} alignItems="center" gap="8px" alignSelf="stretch">
                <TripleCurrencyLogo currency0={currency0} currency1={currency1} currency2={currency2} size={24} margin={12} />
                <Text fontSize="18px">{currency0?.symbol} - {currency1?.symbol}{currency2 ? ` - ${currency2.symbol}` : ''}</Text>
                {
                    expanded
                        ? <ChevronUpIcon color="textSubtle" ml="auto" width={28} onClick={(e: any) => {
                            e.stopPropagation()
                            setExpanded(false)
                        }} />
                        : <ChevronDownIcon color="textSubtle" ml="auto" width={28} />
                }
            </FlexGap>
            {
                expanded
                    ? <>
                        <Hr />
                        <Flex alignItems="center" justifyContent="space-between" mt={3}>
                            <Text fontSize={14} color="textSubtle">{t("APR")}</Text>
                            <Balance fontSize={14} value={pool.apr7d} unit="%" decimals={2} />
                        </Flex>
                        <Flex alignItems="center" justifyContent="space-between" mt={3}>
                            <Text fontSize={14} color="textSubtle">{t("Total Value")}</Text>
                            <Balance fontSize={14} value={totalUSDValue} prefix="~$" decimals={2} />
                        </Flex>
                        <FlexGap gap="8px" alignItems="center" mt={3}>
                            <CurrencyLogo currency={currency0} size="16px" />
                            <Text fontSize={14} color="textSubtle">{t("Pooled %symbol%", { symbol: currency0?.symbol })}</Text>
                            <Balance fontSize={14} value={token0Deposited} ml="auto" decimals={4} />
                        </FlexGap>
                        <FlexGap gap="8px" alignItems="center" mt={3}>
                            <CurrencyLogo currency={currency1} size="16px" />
                            <Text fontSize={14} color="textSubtle">{t("Pooled %symbol%", { symbol: currency1?.symbol })}</Text>
                            <Balance fontSize={14} value={token1Deposited} ml="auto" decimals={4} />
                        </FlexGap>
                        <FlexGap gap="8px" alignItems="center" mt={3}>
                            <CurrencyLogo currency={currency2} size="16px" />
                            <Text fontSize={14} color="textSubtle">{t("Pooled %symbol%", { symbol: currency2?.symbol })}</Text>
                            <Balance fontSize={14} value={token2Deposited} ml="auto" decimals={4} />
                        </FlexGap>
                        <Flex alignItems="center" justifyContent="space-between" mt={3}>
                            <Text fontSize={14} color="textSubtle">{t("Share in Pool")}</Text>
                            <Balance fontSize={14} value={share * 100} unit="%" decimals={2} />
                        </Flex>
                        <FlexGap gap="8px" alignItems="baseline" mt={3}>
                            <Text fontSize={14}>{t("Recieved:")}</Text>
                            <Balance fontSize={16} value={userBalance} unit=" pStable LP" decimals={0} />
                            <CopyButton
                                width="16px"
                                buttonColor="textSubtle"
                                text={lpToken.address}
                                tooltipMessage={t('Token address copied')}
                            />
                            <AddToWalletButton
                                variant="text"
                                p="0"
                                height="auto"
                                width="fit-content"
                                tokenAddress={lpToken.address}
                                tokenSymbol="pStable LP"
                                tokenDecimals={lpToken.decimals}
                                tokenLogo={`https://pdex.vision/images/symbols/pusd.png`}
                            />
                        </FlexGap>
                        <FlexGap gap="8px" mt={3} justifyContent="space-between">
                            <Button width="100%" style={{ fontSize: "16px" }} onClick={handleIncreaseModal}>{t("Add")}</Button>
                            <Button width="100%" style={{ fontSize: "16px" }} onClick={handleOpenDecreaseModal} variant="subtle">{t("Remove")}</Button>
                        </FlexGap>
                    </>
                    : <FlexGap gap="8px" alignItems="baseline" mt={3} onClick={() => setExpanded(true)}>
                        <Text fontSize={14}>{t("Recieved:")}</Text>
                        <Balance fontSize={16} value={userBalance} unit=" pStable LP" decimals={0} />
                        <CopyButton
                            width="16px"
                            buttonColor="textSubtle"
                            text={lpToken.address}
                            tooltipMessage={t('Token address copied')}
                        />
                        <AddToWalletButton
                            variant="text"
                            p="0"
                            height="auto"
                            width="fit-content"
                            tokenAddress={lpToken.address}
                            tokenSymbol="pStable LP"
                            tokenDecimals={lpToken.decimals}
                            tokenLogo={`https://pdex.vision/images/symbols/pusd.png`}
                        />
                    </FlexGap>
            }
        </Panel>
    )
}

export default StablePosition